import type { EBillingAccount } from "@/modules/billing/billingLogic.tsx";
import type { PayableInvoiceId } from "@/modules/billing/payment/PaymentParams.ts";
import type { Translatable } from "@/modules/l10n/getTransText.ts";
import { Trans } from "@lingui/react/macro";
import type {
    BillingAccount,
    BillingAccountInvoice,
    BillingAccountUsage,
    InvoiceRecord,
} from "@warrenio/api-spec/spec.oats.gen";
import { InvoiceStatusEnum } from "@warrenio/api-spec/spec/billingEnums";

/** Common fields shared by REST API & GraphQL */
export interface CommonInvoice {
    id: number | string;
    padded_id?: string | null;
    document_number?: string | null;
    document_type?: string | null;
    type: string;
}

export function getIsCreditNote({ document_type }: CommonInvoice) {
    return document_type === "credit_note";
}

export function getIsReport({ document_type }: CommonInvoice) {
    return document_type === "report";
}

export function getIsCurrentUsage({ id }: CommonInvoice) {
    return id === "current";
}

export function hasPdfDownload(item: CommonInvoice) {
    return !!item.document_number;
}

const fakeRecordTag = Symbol.for("usageToRecord/fakeRecord");

function usageToRecord(usage: BillingAccountUsage): InvoiceRecord {
    return {
        ...usage,
        created: Date.now(),
        [fakeRecordTag as any]: true,
    };
}

export function createCurrentUsage(
    usage: BillingAccountUsage[],
    billingAccount: BillingAccount,
): BillingAccountInvoice {
    const date = new Date();
    const y = date.getFullYear();
    const m = date.getMonth();
    const startDate = new Date(y, m, 1);
    const endDate = new Date(y, m + 1, 0);

    const { running_totals, vat_percentage, id } = billingAccount;
    const { credit_amount, discount_amount, subtotal, total, total_before_tax, vat_tax } = running_totals;

    return {
        id: "current",
        type: "cloud_services",
        is_current: true,
        // Set it to empty so translated default will be used
        name: "",
        records_list: usage.map(usageToRecord),
        period_start: startDate.getTime(),
        period_end: endDate.getTime(),
        created: date.getTime(),
        due_date: 0,
        status: InvoiceStatusEnum.IN_PROGRESS,
        vat_percentage,
        has_record_level_discount: true,
        totals: {
            credit: credit_amount,
            subtotal,
            total,
            total_before_tax,
            vat_tax,
            discount_amount,
            // TODO: waiting for BE amends?
            subtotal_resource_usage: 0,
        },
        billing_account_id: id,
    };
}

export function reportIdTitle({ document_number, name }: BillingAccountInvoice): string | undefined {
    return document_number ?? name;
}

export function invoiceName(invoice: BillingAccountInvoice, variant: "short" | "long"): Translatable {
    const { name, is_current, document_type } = invoice;
    const id = reportIdTitle(invoice);
    if (id) {
        if (variant === "short") return id;

        switch (document_type) {
            case "invoice":
                return <Trans>Invoice {id}</Trans>;
            case "credit_note":
                return <Trans>Credit Note {id}</Trans>;
            default:
                return <Trans>Report {id}</Trans>;
        }
    }
    if (!name && is_current) {
        return <Trans>Current usage</Trans>;
    }
    return name ?? "-";
}

export function getPayableInvoiceId(
    invoice: BillingAccountInvoice,
    billingAccount: EBillingAccount,
): PayableInvoiceId | null {
    if (
        billingAccount.isPostPay &&
        !getIsReport(invoice) &&
        invoice.status === InvoiceStatusEnum.UNPAID &&
        invoice.id !== "current"
    ) {
        return invoice.id;
    }

    return null;
}
